<template>
  <b-card>
    <b-row>
      <b-col>
        <b-row align-h="between">
          <b-col cols="auto">
            <b-row>
              <b-col cols="auto">
                <b-avatar variant="primary" size="40" class="shadow mb-2" v-b-tooltip.hover.top="description">
                  <i class="flaticon-questions-circular-button text-white cursor-pointer icon-2x" />
                </b-avatar>
              </b-col>
              <b-col align-self="center" cols="auto">
                <h1>Average payments</h1>
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="auto">
            <b-dropdown right text="Filters" variant="primary" boundary="window">
              <b-dropdown-form>
                <b-form-checkbox-group v-model="selected">
                  <template v-if="availableFilters.length > 0">
                    <b-form-checkbox 
                      v-for="filter in availableFilters" 
                      :key="filter.title" 
                      :value="filter.title"
                    >
                      {{ filter.title }}
                    </b-form-checkbox>
                  </template>
                  <template v-else>
                    <p class="text-muted">No hay filtros disponibles</p>
                  </template>
                </b-form-checkbox-group>
              </b-dropdown-form>
            </b-dropdown>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <!-- Contador $30, membresías y promedio por tipo -->
    <b-row v-if="showCount30 || showMembership || byTypeRows.length || count30DoctorRows.length" class="mb-3">
      <b-col v-if="showCount30" cols="12" md="6" class="mb-2">
        <div class="meta-panel">
          <h6 class="meta-title mb-2">Visits closed at $30</h6>
          <div class="d-flex justify-content-around text-center">
            <div>
              <div class="meta-value">{{ count30.np || 0 }}</div>
              <div class="meta-label">NP</div>
            </div>
            <div>
              <div class="meta-value">{{ count30.nc || 0 }}</div>
              <div class="meta-label">NC</div>
            </div>
          </div>
          <p class="meta-hint mb-0 mt-2">
            Excluded from Avg NP / Avg NC; still counted as patients.
          </p>
        </div>
      </b-col>
      <b-col v-if="showMembership" cols="12" md="6" class="mb-2">
        <div class="meta-panel">
          <h6 class="meta-title mb-2">Avg memberships</h6>
          <div class="text-center">
            <div class="meta-value">${{ formatMoney(membershipSummary.average) }}</div>
            <div class="meta-label">{{ membershipSummary.count || 0 }} visits · ${{ formatMoney(membershipSummary.total) }}</div>
          </div>
        </div>
      </b-col>
      <b-col v-if="byTypeRows.length" cols="12" class="mb-2">
        <div class="meta-panel">
          <h6 class="meta-title mb-2">Average by visit type</h6>
          <div class="table-responsive">
            <table class="table table-sm table-borderless mb-0 by-type-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th class="text-right">Count</th>
                  <th class="text-right">Total</th>
                  <th class="text-right">Average</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in byTypeRows" :key="row.type_visits_id || row.prefix">
                  <td>
                    <span class="font-weight-semibold">{{ row.prefix }}</span>
                    <span v-if="row.name" class="text-muted small ml-1">{{ row.name }}</span>
                  </td>
                  <td class="text-right">{{ row.count }}</td>
                  <td class="text-right">${{ formatMoney(row.total) }}</td>
                  <td class="text-right">${{ formatMoney(row.average) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </b-col>
      <b-col v-if="count30DoctorRows.length" cols="12" class="mb-2">
        <div class="meta-panel">
          <h6 class="meta-title mb-2">$30 by doctor and facility</h6>
          <div class="table-responsive">
            <table class="table table-sm table-borderless mb-0 by-type-table">
              <thead>
                <tr>
                  <th>Doctor</th>
                  <th>Facility</th>
                  <th class="text-right">NP</th>
                  <th class="text-right">NC</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in count30DoctorRows"
                  :key="`${row.doctor_id}-${row.facility_id}`"
                >
                  <td>{{ row.doctor_name || row.doctor_id }}</td>
                  <td>{{ row.facility_name || row.facility_id }}</td>
                  <td class="text-right">{{ row.np_30_count || 0 }}</td>
                  <td class="text-right">{{ row.nc_30_count || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row class="mb-4">
      <b-col cols="12">
        <vue-apex-charts :options="chartOptions" :series="series" height="450" type="bar" />
      </b-col>
    </b-row>

    <b-row v-if="avgs.length > 0">
      <b-col 
        v-for="({ label, value }, key) in avgs" 
        :key="key"
        cols="12" 
        sm="6" 
        md="4" 
        lg="4"
        class="mb-3"
      >
        <div class="radial-chart-container">
          <div class="chart-wrapper">
            <vue-apex-charts 
              :options="radialBarOptions(label, value)" 
              :series="[percentage(value)]" 
              type="radialBar" 
              height="200"
            />
          </div>
          <div class="chart-label">
            <h6 class="text-center mb-0">{{ getCategoryName(label) }}</h6>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import { BFormCheckboxGroup, BDropdownForm, BFormCheckbox, BCard, BAvatar } from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import { readDailyAverageValue } from '@/core/utils/dailyAverageFields'

export default {
  components: {
    BFormCheckboxGroup,
    VueApexCharts,
    BCard,
    BAvatar,
    BDropdownForm,
    BFormCheckbox
  },
  props: {
    averages: {
      type: [Array, Object],
      default: () => [],
      required: true
    },
    dates: {
      type: Object,
      default: () => { },
      required: true
    },
    /** { np, nc } — visitas NP/NC que cerraron en $30 exactos (A2) */
    count30: {
      type: Object,
      default: null,
    },
    /** average.by_type del backend (A3) */
    byType: {
      type: Array,
      default: () => [],
    },
    /** average.summary.membership — { average, count, total } */
    membershipSummary: {
      type: Object,
      default: null,
    },
    /** average.np_nc_30_by_doctor_facility */
    count30ByDoctor: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    showCount30() {
      if (!this.count30) return false
      return this.count30.np != null || this.count30.nc != null
    },
    showMembership() {
      const row = this.membershipSummary
      if (!row) return false
      return row.average != null || row.count != null || row.total != null
    },
    count30DoctorRows() {
      if (!Array.isArray(this.count30ByDoctor)) return []
      return this.count30ByDoctor.filter(
        row => (parseInt(row.np_30_count, 10) || 0) > 0 || (parseInt(row.nc_30_count, 10) || 0) > 0
      )
    },
    byTypeRows() {
      if (!Array.isArray(this.byType)) return []
      return this.byType.filter(
        row => (parseInt(row.count, 10) || 0) > 0 || (parseFloat(row.total) || 0) > 0
      )
    },
    availableFilters() {
      const filters = []
      
      // Si tenemos datos en el nuevo formato
      if (this.averages && typeof this.averages === 'object' && !Array.isArray(this.averages)) {
        const newDataFields = [
          { key: 'cv_daily', label: 'CV', title: 'CV Promedio' },
          { key: 'fu_daily', label: 'FU', title: 'FU Promedio' },
          { key: 'nc_daily', label: 'NC', title: 'NC Promedio' },
          { key: 'np_daily', label: 'NP', title: 'NP Promedio' },
          { key: 'pi_daily', label: 'PI', title: 'PI Promedio' },
          { key: 'pp_daily', label: 'PP', title: 'PP Promedio' },
          { key: 'rs_daily', label: 'RS', title: 'RS Promedio' },
          { key: 'total_daily', label: 'TOTAL', title: 'TOTAL Promedio' }
        ]

        newDataFields.forEach(({ key, label, title }) => {
          if (this.averages[key] && this.averages[key].length > 0) {
            filters.push({ key, label, title })
          }
        })
      } else if (Array.isArray(this.averages)) {
        // Formato anterior
        this.averages.forEach(({ title, label }) => {
          filters.push({ title, label })
        })
      }
      
      return filters
    },
  },
  watch: {
    averages() {
      this.filterData()
    },
    selected() {
      this.filterData(true)
    }
  },
  methods: {
    getCategoryName(label) {
      // Mapeo de etiquetas a nombres completos
      const categoryNames = {
        'CV': 'Covid 19 Vacunación',
        'FU': 'Follow Up', 
        'NC': 'New Consultation',
        'NP': 'New patient',
        'PI': 'Pending Injection',
        'PP': 'Pending procedure',
        'RS': 'Result',
        'TOTAL': 'Total',
        // También manejar casos con prefijo AVG_
        'AVG_CV': 'Covid 19 Vacunación',
        'AVG_FU': 'Follow Up', 
        'AVG_NC': 'New Consultation',
        'AVG_NP': 'New patient',
        'AVG_PI': 'Pending Injection',
        'AVG_PP': 'Pending procedure',
        'AVG_RS': 'Result',
        'AVG_TOTAL': 'Total'
      };
      return categoryNames[label] || label;
    },
    radialBarOptions(label, value) {
      let color;
      const numValue = parseFloat(value) || 0;
      
      // Nueva escala de colores basada en el valor real
      if (numValue >= 0 && numValue < 100) {
        color = '#FF6B6B'; // Rojo opaco
      } else if (numValue >= 100 && numValue < 250) {
        color = '#FFD93D'; // Amarillo opaco
      } else if (numValue >= 250) {
        color = '#6BCF7F'; // Verde opaco
      } else {
        color = '#E0E0E0'; // Gris para valores inválidos
      }
      return {
        chart: {
          height: 200,
          type: 'radialBar',
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 225,
            hollow: {
              margin: 0,
              size: '60%',
              background: '#fff',
              imageOffsetX: 0,
              imageOffsetY: 0,
              position: 'front',
              dropShadow: {
                enabled: true,
                top: 2,
                left: 0,
                blur: 3,
                opacity: 0.2
              }
            },
            track: {
              background: '#f1f1f1',
              strokeWidth: '67%',
              margin: 0,
              dropShadow: {
                enabled: false
              }
            },
            dataLabels: {
              show: true,
              name: {
                show: true,
                color: '#666',
                fontSize: '11px',
                fontWeight: 600
              },
              value: {
                formatter: function (val) {
                  return value;
                },
                color: '#333',
                fontSize: '16px',
                fontWeight: 'bold',
                show: true,
              }
            }
          }
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            type: 'horizontal',
            shadeIntensity: 0.5,
            gradientToColors: [color], // Usar el color calculado
            inverseColors: true,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100],
            colorStops: [
              {
                offset: 0,
                color: color, // Usar el mismo color calculado
                opacity: 1
              },
              {
                offset: 100,
                color: color, // Usar el mismo color calculado
                opacity: 1
              }
            ]
          }
        },
        stroke: {
          lineCap: 'round'
        },
        labels: [label],
      }
    },
    percentage(valor) {
      const numValor = parseFloat(valor) || 0;
      if (numValor < 0) return 0;
      if (numValor > 1000) return 100;
      return (numValor / 1000) * 100; // Calcula el porcentaje en función del valor y 1000.
    },
    // Método para procesar datos diarios del nuevo formato
    processDailyData(dailyData, fieldName) {
      const data = []
      dailyData.forEach(item => {
        const y = readDailyAverageValue(item, fieldName)
        const x = `${item.month}-${item.day}-${item.year}`
        data.push({ x, y })
      })
      return data
    },
    // Método para obtener el valor promedio de un campo diario
    getAverageValue(dailyData, fieldName) {
      if (!dailyData || !dailyData.length) {
        return 0
      }
      const sum = dailyData.reduce(
        (acc, item) => acc + readDailyAverageValue(item, fieldName),
        0
      )
      const result = (sum / dailyData.length).toFixed(2)
      return result
    },
    filterData(selected = false) {
      const series = []
      this.avgs = []

      // Si tenemos datos en el nuevo formato, procesarlos
      if (this.averages && typeof this.averages === 'object' && !Array.isArray(this.averages)) {
        const newDataFields = [
          { key: 'cv_daily', label: 'CV', avgField: 'AVG_CV', title: 'CV Promedio' },
          { key: 'fu_daily', label: 'FU', avgField: 'AVG_FU', title: 'FU Promedio' },
          { key: 'nc_daily', label: 'NC', avgField: 'AVG_NC', title: 'NC Promedio' },
          { key: 'np_daily', label: 'NP', avgField: 'AVG_NP', title: 'NP Promedio' },
          { key: 'pi_daily', label: 'PI', avgField: 'AVG_PI', backendKey: 'pi', title: 'PI Promedio' },
          { key: 'pp_daily', label: 'PP', avgField: 'AVG_PP', title: 'PP Promedio' },
          { key: 'rs_daily', label: 'RS', avgField: 'AVG_RS', backendKey: 'rs', title: 'RS Promedio' },
          { key: 'total_daily', label: 'TOTAL', avgField: 'AVG_TOTAL', title: 'TOTAL Promedio' }
        ]

        newDataFields.forEach(({ key, label, avgField, backendKey, title }) => {
          if (this.averages[key] && this.averages[key].length > 0) {
            if (selected && this.selected.length && !this.selected.includes(title)) return;

            const backendValue = backendKey ? this.averages[backendKey] : null;
            const avgValue = backendValue != null && backendValue !== ''
              ? backendValue
              : this.getAverageValue(this.averages[key], avgField);

            this.avgs.push({ label, value: avgValue })
            
            const data = this.processDailyData(this.averages[key], avgField)
            series.push({ name: label, data })
          }
        })
      } else {
        // Procesar datos en el formato anterior
        if (Array.isArray(this.averages)) {
          this.averages.forEach(({ title, averages, label }) => {
            if (selected && this.selected.length && !this.selected.includes(title)) return;

            const summaryValue = parseFloat(title.split('$')[1]) || 0
            const useBackendSummary = label === 'AVG_PI' || label === 'AVG_RS'

            this.avgs.push({ label, value: String(summaryValue) })
            const data = []
            averages.forEach(point => {
              const { day, month, year } = point || {}
              const y =
                useBackendSummary && averages.length === 1
                  ? summaryValue
                  : readDailyAverageValue(point, label)
              const x = `${month}-${day}-${year}`
              data.push({ x, y })
            })
            series.push({ name: label, data })
          })
        }
      }
      
      this.series = series
    },
    verifyDates({ start, end }) {
      const fecha1 = new Date(start)
      const fecha2 = new Date(end)
      // Calcula la diferencia en milisegundos
      const diferenciaMs = Math.abs(fecha2 - fecha1);

      // Convierte la diferencia de milisegundos a días
      const diferenciaDias = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24));

      // Verifica si la diferencia es igual o menor a 1 día
      return diferenciaDias <= 1;
    },
    formatMoney(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '0'
      return n % 1 === 0
        ? String(n)
        : n.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
    },
  },
  mounted() {
    this.filterData();
  },
  data() {
    return {
      /*
        1: line
        2: bar
      */
      avgs: [],
      series: [],
      selected: [],
      description: "Averages diarios del periodo de la fecha selecionada",
      chartOptions: {
        xaxis: {
          type: 'datetime',
          labels: {
            datetimeUTC: false,
            format: 'dd-MM-yyyy',
          }
        },
        chart: {
          type: 'bar',
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            columnWidth: '55%',
            borderRadius: 2,
          },
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 1,
        },
        fill: {
          opacity: 1,
        },
      },
      barChartOptions: {
        plotOptions: {
          bar: {
            borderRadius: 10,
            dataLabels: {
              position: 'top', // top, center, bottom
            },
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val;
          },
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ["#304758"]
          }
        },

        xaxis: {
          categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
          position: 'top',
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          },
          crosshairs: {
            fill: {
              type: 'gradient',
              gradient: {
                colorFrom: '#D8E3F0',
                colorTo: '#BED1E6',
                stops: [0, 100],
                opacityFrom: 0.4,
                opacityTo: 0.5,
              }
            }
          },
          tooltip: {
            enabled: true,
          }
        },
        yaxis: {
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false,
          },
          labels: {
            show: false,
            formatter: function (val) {
              return val + "%";
            }
          }

        },
      },
      seriesRadial: [75],
    }
  }
};
</script>

<style scoped>
.meta-panel {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 14px 16px;
  height: 100%;
}

.meta-title {
  color: #495057;
  font-weight: 600;
  font-size: 0.95rem;
}

.meta-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e88e5;
  line-height: 1.2;
}

.meta-label {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 600;
}

.meta-hint {
  font-size: 0.75rem;
  color: #868e96;
}

.by-type-table th {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 600;
  border-bottom: 1px solid #dee2e6 !important;
}

.by-type-table td {
  font-size: 0.85rem;
  vertical-align: middle;
  padding-top: 0.35rem;
  padding-bottom: 0.35rem;
}

.radial-chart-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 15px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 280px;
}

.radial-chart-container:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.chart-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
}

.chart-label {
  width: 100%;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  min-height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
}

.chart-label h6 {
  color: #495057;
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 1.2;
  margin: 0;
  text-align: center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .radial-chart-container {
    padding: 5px;
  }
}
</style>